import React, { useEffect, useRef, useState } from "react";
import { Box, Spinner, Heading } from "@chakra-ui/react";
import axios from "axios";
import echarts from "./lib";

// Additional Analytics section — secondary colour is the section's PURPLE
// accent (#6B4E9B, brightened), per Michael's per-section colour rule.
const PURPLE = "#8A63D2";
const PURPLE_DEEP = "#6B4E9B";

const fmtShort = (value) => {
  if (value >= 1000000) return `R${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `R${(value / 1000).toFixed(0)}K`;
  return `R${value.toLocaleString("en-US")}`;
};

// `region` is optional — when set (CHK region tabs), the region endpoint and
// request body are used, exactly as the old Chart4Region.js did.
const Chart4 = ({ supplier, retailer, region }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let disposed = false;
    const controller = new AbortController();

    const initChart = async () => {
      if (!containerRef.current) return;
      setLoading(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/rep_sales/${region ? "chart4_chk_region" : "chart4"}`,
          region
            ? { supplier, retailer, region, userid: localStorage.getItem("userid") }
            : { supplier, retailer, userid: localStorage.getItem("userid") },
          { signal: controller.signal }
        );

        // Identical transforms to the Chart.js version — the DATA must not
        // change: same en-US month labels, values used raw (no rounding).
        const rows = res.data.data;
        const labels = rows.map((i) =>
          new Date(i.date).toLocaleDateString("en-US", { month: "short", year: "numeric" })
        );
        const values = rows.map((i) => i.value);

        if (disposed || !containerRef.current) return;

        const avg = values.length
          ? values.reduce((a, v) => a + v, 0) / values.length
          : 0;

        const chart = echarts.init(containerRef.current);
        chartRef.current = chart;
        chart.setOption({
          grid: { left: 8, right: 14, top: 30, bottom: 4, containLabel: true },
          xAxis: {
            type: "category",
            data: labels,
            axisLabel: { color: "#64798A", fontSize: 10.5, rotate: 45 },
            axisLine: { lineStyle: { color: "#E2E9EE" } },
            axisTick: { show: false },
          },
          yAxis: {
            type: "value",
            axisLabel: { color: "#64798A", fontSize: 11, formatter: fmtShort },
            splitLine: { lineStyle: { color: "rgba(229,231,235,0.5)", type: [3, 3] } },
          },
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow", shadowStyle: { color: "rgba(138,99,210,0.07)" } },
            confine: true,
            backgroundColor: "#ffffff",
            borderColor: "#E2E9EE",
            borderWidth: 1,
            padding: [8, 12],
            textStyle: { color: "#1D2F3A", fontSize: 12 },
            extraCssText: "box-shadow: 0 6px 18px rgba(29,47,58,0.14); border-radius: 8px;",
            formatter: (params) => {
              const p = params[0];
              const i = p.dataIndex;
              let delta = "";
              if (i > 0 && values[i - 1]) {
                const ch = ((values[i] - values[i - 1]) / values[i - 1]) * 100;
                const up = ch >= 0;
                delta = `<br/><span style="color:${up ? "#12775F" : "#BF3F38"}">${
                  up ? "▲" : "▼"
                } ${Math.abs(ch).toFixed(1)}% vs prev month</span>`;
              }
              return `${p.name}<br/><b>R${Number(p.value).toLocaleString("en-US")}</b>${delta}`;
            },
          },
          series: [
            {
              type: "bar",
              data: values,
              barWidth: "62%",
              itemStyle: {
                borderRadius: [5, 5, 0, 0],
                color: {
                  type: "linear",
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: "rgba(20,163,178,0.95)" },
                    { offset: 1, color: "rgba(20,163,178,0.28)" },
                  ],
                },
              },
              emphasis: {
                itemStyle: {
                  color: {
                    type: "linear",
                    x: 0,
                    y: 0,
                    x2: 0,
                    y2: 1,
                    colorStops: [
                      { offset: 0, color: "rgba(138,99,210,0.95)" },
                      { offset: 1, color: "rgba(138,99,210,0.3)" },
                    ],
                  },
                  shadowBlur: 10,
                  shadowColor: "rgba(29,47,58,0.22)",
                },
              },
              label: {
                show: true,
                position: "top",
                formatter: (p) => fmtShort(p.value),
                color: "#4A5D6B",
                fontSize: 10.5,
                fontWeight: 700,
                fontFamily: "Inter, sans-serif",
              },
              labelLayout: { hideOverlap: true },
              markLine: {
                silent: true,
                symbol: "none",
                lineStyle: { color: PURPLE, type: "dashed", width: 1.5 },
                label: {
                  position: "insideEndTop",
                  formatter: () => `avg ${fmtShort(avg)}`,
                  color: PURPLE_DEEP,
                  fontSize: 10.5,
                  fontWeight: 600,
                },
                data: [{ yAxis: avg }],
              },
              animationDuration: 800,
              animationDelay: (idx) => idx * 40,
              animationEasing: "cubicOut",
            },
          ],
        });
      } catch (error) {
        if (!axios.isCancel(error)) console.error("chart4 fetch failed", error);
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    initChart();

    const ro = new ResizeObserver(() => {
      if (chartRef.current) chartRef.current.resize();
    });
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      disposed = true;
      controller.abort();
      ro.disconnect();
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [supplier, retailer, region]);

  return (
    <Box height="100%" display="flex" flexDirection="column" position="relative">
      <Heading size="xs" color="gray.600" mb={2} fontWeight="600">
        {region ? `Monthly Sales - Values (${region})` : "Monthly Sales (Values)"}
      </Heading>
      <Box flex="1" position="relative">
        <div
          ref={containerRef}
          style={{ width: "100%", height: "100%", overflow: "hidden" }}
        ></div>
        {loading && (
          <Spinner
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
          />
        )}
      </Box>
    </Box>
  );
};

export default Chart4;
